import { Formik } from 'formik';
import { useEffect, useState, useRef } from 'react';
import LoanSimulationService from '../../services/LoanSimulation';
import { LoanSimulation, stateType } from '../../interfaces/LoanSimulation';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES_PATH, VALIDATOR_PATTERN } from '../../constants';
import MerchantService from '../../services/Merchant/index';
import { GoodTypes } from '../../interfaces/MerchantInfo';
import {
  EntryForm,
  TodayHighlight,
  TodayHighlightText,
  FirstFeeText,
  FirstFee,
  RegularFeeText,
  DetailsSectionSubtitle,
  WrapperBox,
  WrapperBoxElement,
  WrapperBoxSection,
  ProdTitle,
  SummaryWrapper,
  SummaryData,
  TextBoldSpan,
  EntryContainer,
  EntryMainButton,
  FormInputNumber,
  RequestAmount,
  RequestAmountBold,
  FormSelectInput,
  RfnEuroIcon,
  RfnSeparationLine,
  TextMediumTitle,
  TotalAmountBold,
  ProdAmpliationTitle,
  RfnContainer,
} from './StyledComponents';
import {
  FormInputRow,
  FormFloating,
  FormOption,
  FormInputLabel,
  ValidationError,
} from '../../styles/FormStyledComponents';
import { MonthText } from '../../styles/GlobalStyledComponents';

import EuroImg from '../../assets/images/euro-icon.svg';
import { useCreditProposalResult } from '../../contexts/CreditProposalResultContext';
import InsightService from '../../services/Insight';

import { CommercialOfferType } from '../../services/LoanSimulation/types';
import dayjs from 'dayjs';
import { blockCharacters, transformAmount } from '../../utils/formInputHelper';
import CreditProposalService from '../../services/CreditProposal';
import { RefinanceCreditProposal } from '../../interfaces/Refinance';
import {
  CommercialOffer,
  CommercialOfferInfo,
  FlowType,
} from '../../interfaces/CommercialOffer';
import { CreditProposalResult } from '../../interfaces/CreditProposalResult';
import { CustomerClassificationEnum } from '../../interfaces/CustomerClassification';
import { useCustomerClassification } from '../../contexts/CustomerClassificationContext';
import { validateSusAmount } from '../../utils/validators';
import AnalyticsService from '../../services/Analytics';
import { DenialStep } from '../../interfaces/Analytics';
import { useCustomer } from '../../contexts/CustomerContext';
import {
  getCommercialOffersStored,
  setCommercialOffersStored,
} from '../../utils/commercialOffersHelper';

let abortController = new AbortController();

interface EntrySusDataProps {
  loadingEvent: (data: boolean) => void;
}

export const EntrySUS: React.FC<EntrySusDataProps> = ({ loadingEvent }) => {
  let navigate = useNavigate();
  const { state } = useLocation();
  const { creditProposalResult, createCreditProposalResult } =
    useCreditProposalResult();
  const { createCustomerClassification } = useCustomerClassification();
  const { createCustomer } = useCustomer();
  const [term, setTerm] = useState(0);
  const [amount, setAmount] = useState<string>('');
  const [pendingAmount, setPendingAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<string>('');
  const { loanId, refinancedProposal } = (state as stateType) || {};
  const [inputTyped, setInputTyped] = useState<string>('');
  const [simulationSelected, setSimulationSelected] =
    useState<LoanSimulation | null>(null);
  const [products, setProducts] = useState<GoodTypes[] | undefined>(undefined);
  const [productSelected, setProductSelected] = useState<GoodTypes | undefined>(
    undefined
  );
  const [amountError, setAmountError] = useState(false);
  const [startTransition, setStartTransition] = useState(false);
  const [commercialOffersList, setCommercialOffersList] = useState<
    CommercialOfferType[] | undefined
  >();
  const [commercialOfferSelected, setCommercialOfferSelected] = useState<
    CommercialOfferType | undefined
  >(undefined);
  const [showSummary, setShowSummary] = useState(false);
  const [termList, setTermList] = useState<number[]>([]);
  const [termError, setTermError] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [hideTermList, setHideTermList] = useState(true);
  const [merchantId, setMerchantId] = useState<string>('');

  const amountLimitSUS = 200;

  const initProductValue = {
    product: '',
    amount: '',
    termNumber: '0',
  };
  const [productValue, setProductValue] = useState(initProductValue);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (inputTyped) {
      const data = setTimeout(() => {
        // if amount didn´t change
        if (amount === inputTyped) {
          setHideTermList(false);
        } else {
          setAmount(inputTyped);
        }
      }, 1000);
      return () => clearTimeout(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputTyped]);

  useEffect(() => {
    loadingEvent(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (commercialOffersList && commercialOffersList.length > 0) {
      const filteredList: CommercialOfferType[] = [];
      const offersList = [...commercialOffersList];
      offersList?.forEach((elem) => {
        const filteredF = elem.financial_offers.filter((e) => e.term === term);
        const newElem = elem;
        newElem.financial_offers = filteredF;
        if (newElem.financial_offers.length > 0) {
          filteredList.push(newElem);
        }
      });
      filteredList[0] && setCommercialOfferSelected(filteredList[0]);
      let selection;
      if (filteredList[0]) {
        selection = filteredList[0];
      }
      if (selection) {
        InsightService.logTrace('LoanSimulation', [
          { commercialOfferId: selection?.commercial_offer_id },
          { amount: selection.financial_offers[0].total_cost.toString() },
          { term: selection.financial_offers[0].term.toString() },
        ]);
        setSimulationSelected(selection.financial_offers[0]);
      }
      setShowSummary(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  useEffect(() => {
    getProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    state && formSubmit(productValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productValue]);

  const handleAmountInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    handleChange: any
  ) => {
    let { value } = e.target;
    if (VALIDATOR_PATTERN.ENTRY_AMOUNT.test(value) || value === '') {
      if (value.includes(',') || value.includes('.')) {
        if (value.includes(',')) {
          value = value.replace(',', '.');
        }
        //Comprueba los decimales introducidos y limita a 2
        const decimals = value.split('.')[1];
        if (decimals.length <= 2) {
          newAmountOk(value);
          handleChange(e);
        } else {
          setAmountError(true);
        }
      } else {
        newAmountOk(value);
        handleChange(e);
      }
    } else if (value !== '') {
      setAmountError(true);
    }
  };

  // Debounced version, para prevenir múltiples llamadas a la API y que se cancelen
  const debouncedGetSimulation = (amountSelected: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      getSimulation(amountSelected);
    }, 500);
  };

  const newAmountOk = (value: string) => {
    const amount = parseFloat(value);
    setAmount(value);
    if (amount && pendingAmount && amount > 0) {
      const totalAmount = (amount + pendingAmount).toString();
      setTotalAmount(totalAmount);
      setTerm(0);
      setCommercialOffersList(undefined);
      setShowSummary(false);
      if (amount >= amountLimitSUS) {
        debouncedGetSimulation(totalAmount);
      } else {
        setAmountError(true);
        setHideTermList(true);
        setAmount('');
      }
    } else {
      setAmountError(true);
      setAmount('');
      setTotalAmount('');
      setHideTermList(true);
    }
  };

  // Se comprueba el importe máximo y mínimo permitido por las ofertas comerciales
  const getLimitsCommercialOffer = (
    data: CommercialOffer[],
    amount: number
  ) => {
    let maxAmount = data[0].max_amount;
    let minAmount = data[0].min_amount;

    if (data.length > 1) {
      data.forEach((offer: CommercialOffer) => {
        if (offer.max_amount > maxAmount) {
          maxAmount = offer.max_amount;
        }
        if (offer.min_amount < minAmount) {
          minAmount = offer.min_amount;
        }
      });
    }
    if (amount > maxAmount || amount < minAmount) {
      setAmountError(true);
    }
  };

  const getSimulation = async (amountSelected: string) => {
    try {
      setCommercialOffersList([]);
      setAmountError(false);
      setHideTermList(false);
      abortController.abort();
      abortController = new AbortController();
      let amountT = parseFloat(amountSelected);
      const sim = {
        amount: parseFloat(amountT.toFixed(2)),
        //flows: ["CEX", "PLS"],
        flows: ['CEX', 'PLS', 'SUS'],
      };

      const commercialOffers = await LoanSimulationService.create(sim, {
        signal: abortController.signal,
      });
      setCommercialOffersList(commercialOffers.commercial_offers);
      let simulations: LoanSimulation[];
      const equalOffer = commercialOffers.commercial_offers.find(
        (c: any) =>
          c.commercial_offer_id === creditProposalResult?.commercial_offer_id
      )?.financial_offers;
      if (equalOffer) {
        simulations = equalOffer;
      } else {
        simulations = commercialOffers.commercial_offers[0]?.financial_offers;
      }
      const termsL: number[] = [];
      commercialOffers.commercial_offers.forEach((offer: any) => {
        offer.financial_offers.forEach(
          (e: any) => !termsL.find((t) => t === e.term) && termsL.push(e.term)
        );
      });
      termsL.sort((a, b) => {
        return a - b;
      });
      setTermList(termsL);
      setCommercialOfferSelected(commercialOffers.commercial_offers[0]);

      const flows = localStorage.getItem('pepper-commercial-offers-flows-S');
      const commercialOffersStored = getCommercialOffersStored();

      if (commercialOffersStored) {
        const commercialOfferData: CommercialOffer[] =
          commercialOffersStored.value;
        if (commercialOfferData && commercialOfferData.length > 0) {
          getLimitsCommercialOffer(commercialOfferData, amountT);
        }
      } else if (
        commercialOffers.commercial_offers.length > 0 &&
        flows?.includes('SUS') &&
        !commercialOffersStored
      ) {
        const params: CommercialOfferInfo = {
          currency: 'EUR',
          flow: FlowType.SUS,
          commercialOfferId:
            commercialOffers.commercial_offers[0].commercial_offer_id,
        };
        const response = await MerchantService.getCommercialOfferInfo(params);
        if (response.flow_commercial_offers.length > 0) {
          getLimitsCommercialOffer(
            response.flow_commercial_offers[0].commercial_offers,
            amountT
          );
          setCommercialOffersStored(
            response.flow_commercial_offers[0].commercial_offers,
            merchantId
          );
        }
      } else {
        console.error('Error al obtener las ofertas comerciales');
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
      if (!simulations) {
        setAmountError(true);
        setHideTermList(true);
        setStartTransition(false);
      }
    } catch (error: any) {
      if (error.message !== 'canceled') {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };

  const getAmortizationSimulation = async () => {
    try {
      const response = await LoanSimulationService.simulation(loanId);
      setPendingAmount(
        response.amortization_amount
          ? parseFloat(Number(response.amortization_amount).toFixed(2))
          : 0
      );
    } catch (error: any) {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const getProducts = async () => {
    try {
      const response = await MerchantService.getMerchantInfo();
      const goodTypes = response.good_types;
      setProducts(goodTypes);
      setProductSelected(goodTypes[0]);
      const initProductV = {
        product: creditProposalResult
          ? creditProposalResult.good_type
          : goodTypes[0].code,
        amount: '',
        termNumber: '',
      };
      setProductValue(initProductV);
      setMerchantId(response.merchant_id);
      getAmortizationSimulation();
    } catch (error: any) {
      if (error?.response?.status === 401) navigate(ROUTES_PATH.TIMEOUT);
      else navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const formSubmit = (data: {
    product: string;
    amount: string;
    termNumber: string;
  }) => {
    setProductSelected(
      products?.find((pr: GoodTypes) => pr.code === data.product)
    );
    setInputTyped(data.amount);
    setAmountError(false);
  };

  const newRefinanceLoan = async () => {
    if (refinancedProposal && amount && term && productSelected) {
      setLoading(true);
      const amountNum = parseFloat(amount);
      const request: RefinanceCreditProposal = {
        requested_amount: amountNum,
        purchase_amount: amountNum,
        term: term,
        good_type: productSelected.code,
      };

      try {
        const response = await CreditProposalService.refinances(
          refinancedProposal,
          request
        );
        if (response) {
          createCreditProposalResult(response);
          createCustomer(response.customer);
          getClassification(response);
        }
      } catch (error: any) {
        if (
          error.response?.data?.result?.errors.find(
            (er: any) => er.code === 'DRF0081'
          )
        ) {
          navigate(ROUTES_PATH.EXISTING_RFN);
        } else {
          navigate(ROUTES_PATH.UNKNOWN_ERROR);
        }
      }
    }
  };

  const getClassification = async (creditProposal: CreditProposalResult) => {
    const classification = await CreditProposalService.postClassification(
      creditProposal.credit_proposal_id,
      creditProposal
    );
    createCustomerClassification(classification);
    if (
      classification.customer_classification ===
      CustomerClassificationEnum.denied
    ) {
      AnalyticsService.sendLoanRefused({ denial_step: DenialStep.CUST });
      navigate(ROUTES_PATH.DENIAL);
    } else {
      navigate(ROUTES_PATH.PERSONAL_DATA);
    }
  };

  const handleContinue = () => {
    newRefinanceLoan();
  };

  useEffect(() => {
    if (amountError) {
      setAmount('');
    }
  }, [amountError]);

  return (
    <RfnContainer>
      <ProdTitle id="entry-another-product-title">Producto anterior</ProdTitle>
      <SummaryWrapper>
        <SummaryData>
          <TextMediumTitle $fontSize={12}>Importe solicitado</TextMediumTitle>
          <TextBoldSpan $fontSize={12}>
            {totalAmount && transformAmount(totalAmount)} €
          </TextBoldSpan>
          <TextMediumTitle $fontSize={12}>Pendiente</TextMediumTitle>
          <TextBoldSpan $fontSize={12}>
            {pendingAmount && transformAmount(pendingAmount)} €
          </TextBoldSpan>
        </SummaryData>
      </SummaryWrapper>
      <Formik
        initialValues={productValue}
        onSubmit={formSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, submitForm, handleChange }) => (
          <EntryForm
            name="simulationForm"
            $opacity={startTransition && !amountError ? 0 : 1}
          >
            <EntryContainer>
              <ProdAmpliationTitle id="entry-amount-title">
                Selecciona la ampliación
              </ProdAmpliationTitle>

              <FormInputRow>
                <FormFloating>
                  <FormSelectInput
                    id="entry-product-input"
                    component="select"
                    name="product"
                    key="product"
                    onBlur={() => {
                      setProductSelected(
                        products?.find(
                          (pr: GoodTypes) => pr.code === values.product
                        )
                      );
                    }}
                  >
                    {products?.map((product: GoodTypes) => {
                      return (
                        <FormOption key={product.code} value={product.code}>
                          {product.name}
                        </FormOption>
                      );
                    })}
                  </FormSelectInput>
                  <FormInputLabel htmlFor="product">Producto</FormInputLabel>
                </FormFloating>
              </FormInputRow>
              <FormInputRow $minHeight="38px">
                <FormFloating
                  $halfWidth={
                    commercialOffersList &&
                      commercialOffersList.length > 0 &&
                      !hideTermList
                      ? 'true'
                      : ''
                  }
                >
                  <FormInputNumber
                    id="entry-amount-input"
                    autoComplete="off"
                    type="number"
                    step="any"
                    name="amount"
                    min="0"
                    onKeyDown={blockCharacters}
                    error={
                      amountError || (errors.amount && touched.amount)
                        ? 'true'
                        : ''
                    }
                    validate={validateSusAmount}
                    onKeyUp={(e: any) => {
                      if (values.amount && e.which !== 13) {
                        setHideTermList(true);
                        setAmountError(false);
                        setAmount(values.amount);
                        submitForm();
                      } else if (
                        e.which !== 13 &&
                        parseInt(values.amount) === 0
                      ) {
                        setHideTermList(true);
                      } else {
                        setAmountError(true);
                        setStartTransition(false);
                        setAmount('');
                        setInputTyped('');
                      }
                    }}
                    position={
                      commercialOffersList &&
                        commercialOffersList.length > 0 &&
                        termList
                        ? 'left'
                        : 'upper'
                    }
                    onKeyPress={(e: any) => {
                      if (e.which === 13) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e: any) => {
                      handleAmountInput(e, handleChange);
                    }}
                  />
                  <FormInputLabel
                    htmlFor="amount"
                    $error={
                      amountError || (errors.amount && touched.amount)
                        ? 'true'
                        : ''
                    }
                  >
                    Importe
                  </FormInputLabel>
                  <RfnEuroIcon src={EuroImg}></RfnEuroIcon>
                  {((amountError && touched.amount) ||
                    (errors.amount && touched.amount)) && (
                      <ValidationError>
                        {amountError && !errors.amount
                          ? 'Introduce un importe válido'
                          : errors.amount}
                      </ValidationError>
                    )}
                </FormFloating>
                {commercialOffersList &&
                  commercialOffersList.length > 0 &&
                  !hideTermList && (
                    <FormFloating $halfWidth="true">
                      <FormInputNumber
                        id="entry-term-input"
                        as="select"
                        name="termNumber"
                        defaultValue="0"
                        onChange={(e: any) => {
                          setTerm(parseInt(e.target.value));
                          setTermError(false);
                        }}
                        className={`${termError && 'error'}`}
                      >
                        {commercialOffersList &&
                          termList.map((t) => {
                            return (
                              <FormOption value={t.toString()} key={t}>
                                {t}
                              </FormOption>
                            );
                          })}
                        <FormOption value="0" hidden></FormOption>
                      </FormInputNumber>
                      <FormInputLabel
                        htmlFor="term"
                        $error={termError ? 'true' : ''}
                      >
                        Plazo
                      </FormInputLabel>
                      <MonthText>meses</MonthText>
                    </FormFloating>
                  )}
              </FormInputRow>
            </EntryContainer>
          </EntryForm>
        )}
      </Formik>

      {simulationSelected && showSummary && !amountError && (
        <>
          <ProdTitle id="entry-resume-title">Resumen de la operación</ProdTitle>
          <WrapperBox>
            <WrapperBoxSection>
              <RegularFeeText>Importe nuevo solicitado</RegularFeeText>
              <RequestAmount>
                {amount && transformAmount(amount)} €
              </RequestAmount>
            </WrapperBoxSection>
            <WrapperBoxSection>
              <TotalAmountBold $fontSize={12}>
                Importe total (+Principal pend.)
              </TotalAmountBold>
              <RequestAmountBold>
                {totalAmount && transformAmount(totalAmount)} €
              </RequestAmountBold>
            </WrapperBoxSection>
            <RfnSeparationLine $loading={loading} />
            <WrapperBoxSection>
              <WrapperBoxElement>
                <TodayHighlight>
                  <TodayHighlightText>
                    {commercialOfferSelected?.flow === 'CEX'
                      ? 'Hoy'
                      : dayjs(
                        commercialOfferSelected?.first_due_date,
                        'YYYY-MM-DD'
                      ).format('DD/MM/YYYY')}
                  </TodayHighlightText>
                </TodayHighlight>
                <FirstFeeText>Primer pago</FirstFeeText>
              </WrapperBoxElement>
              <FirstFee>
                {transformAmount(simulationSelected.first_fee)} €
              </FirstFee>
            </WrapperBoxSection>
            <RfnSeparationLine $loading={loading} />
            <WrapperBoxSection>
              <WrapperBoxElement>
                <RegularFeeText>Cuotas restantes ({term - 1})</RegularFeeText>
              </WrapperBoxElement>
              <WrapperBoxElement>
                <RegularFeeText>
                  {transformAmount(simulationSelected.regular_fee)} € /mes
                </RegularFeeText>
              </WrapperBoxElement>
            </WrapperBoxSection>
          </WrapperBox>
          <WrapperBoxSection margin={'8px'}>
            <DetailsSectionSubtitle>
              Com. Apertura {transformAmount(simulationSelected.opening_fee)} €
              primera cuota
              {/* {simulationSelected.opening_fee !== 0 && (
                <DetailsSectionSubtitle>
                  {simulationSelected.opening_fee_type === "F"
                    ? " financiada"
                    : " primera cuota"}
                  {simulationSelected.opening_fee_type === "F" &&
                    ` (${transformAmount(
                      simulationSelected.opening_fee / simulationSelected.term
                    )} € por recibo)`}
                </DetailsSectionSubtitle>
              )} */}
            </DetailsSectionSubtitle>
          </WrapperBoxSection>
        </>
      )}

      <EntryMainButton
        id="entry-continue-btn"
        onClick={handleContinue}
        disabled={amountError || !showSummary}
      >
        Continuar
      </EntryMainButton>
    </RfnContainer>
  );
};
